import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LP_UI_CONFIG } from '../core/tokens';
import { StorageService } from '../services/storage.service';
import { OTObject } from '../models/classes/object';
import { map } from 'rxjs/operators';
import { IOTObject } from '../models/object.model';

/**
 * AssetsApi - Pure Promise-based HTTP calls
 * Handles all HTTP requests for assets, sensors, equipment, plants, and related entities
 *
 * Pure Angular 20 Pattern:
 * - All methods return Promises instead of Observables
 * - Uses firstValueFrom() to convert HTTP calls
 * - Uses inject() for dependency injection
 * - Built-in error handling
 */
@Injectable({ providedIn: 'root' })
export class AssetsApi {
  private readonly cfg = inject(LP_UI_CONFIG);
  private http = inject(HttpClient);
  private storage = inject(StorageService);

  private readonly endpointMap: Record<string, string> = {
    ia: 'api/nav-ia/',
    em: 'api/nav-em/',
  };
  private notificationDataPromise: Promise<any> | null = null;
  private navDataPromise: Promise<any> | null = null;

  private get headers() {
    return {
      Authorization: 'Bearer ' + this.storage.token(),
    };
  }

  // ==================== Navigation ====================

  async getNavigation(forceReload = false): Promise<{
    count: number;
    items: any[];
    storageOptions: any;
  }> {
    const endpoint = this.endpointMap[this.cfg.appName];

    if (!endpoint) {
      throw new Error(`Unsupported appName: ${this.cfg.appName}`);
    }

    const headers: any = { ...this.headers };
    if (forceReload) {
      // Force cache bypass by adding no-cache directive
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
      headers['Expires'] = '0';
    }

    if (this.cfg.appName === 'em') {
      if (!this.navDataPromise) {
        this.navDataPromise = firstValueFrom(
          this.http.get<{ count: number; items: any[]; storageOptions: any }>(
            `${this.cfg.host}${endpoint}`,
            { headers }
          )
        );
      }
      return this.navDataPromise;
    } else {
      return firstValueFrom(
        this.http.get<{ count: number; items: any[]; storageOptions: any }>(
          `${this.cfg.host}${endpoint}`,
          { headers }
        )
      );
    }
  }

  // ==================== Sensors ====================

  async getSensors(): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/sensors/`, {
        headers: this.headers,
      })
    );
  }

  async getSensorColorData(
    macID: string,
    modelID: string,
    assetID: string,
    gatewayID: string
  ): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/get_htst_sensor_availability`,
        {
          MacID: macID,
          ModelID: modelID,
          AssetID: assetID,
          GatewayID: gatewayID,
        },
        { headers: this.headers }
      )
    );
  }

  async postHtstData(
    colorCode: string,
    macId: string,
    modelId: string,
    isOccupied: boolean
  ): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.cfg.host}api/update_htst_sensor_status`,
        {
          MacID: macId,
          ModelID: modelId,
          ColorCode: colorCode,
          IsOccupied: isOccupied,
        },
        { headers: this.headers }
      )
    );
  }

  async getMapBoxKeys(): Promise<{
    satelliteView: string;
    mapView: string;
    geoCodingToken: string;
  }> {
    return firstValueFrom(
      this.http.get<{
        satelliteView: string;
        mapView: string;
        geoCodingToken: string;
      }>(`${this.cfg.host}api/authenticated_frontend_keys`, {
        headers: this.headers,
      })
    );
  }

  async createSensor(params: any): Promise<any[]> {
    params.imageFileName = !params.imageFileName ? '' : params.imageFileName;
    params.imageData = !params.imageData ? '' : params.imageData;
    return firstValueFrom(
      this.http.post<any[]>(`${this.cfg.host}api/create_sensor/`, params, {
        headers: this.headers,
      })
    );
  }

  async reconnectSensor(params: any): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(`${this.cfg.host}api/reconnect_sensor/`, params, {
        headers: this.headers,
      })
    );
  }

  async updateSensor(params: any): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(`${this.cfg.host}api/update_sensor/`, params, {
        headers: this.headers,
      })
    );
  }

  async deleteSensor(sensorID: number): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/delete_sensor/`,
        { sensorID },
        { headers: this.headers }
      )
    );
  }

  async removeDAUFromGatewayMoxa(sensorID: number): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${this.cfg.host}api/remove_dau_from_gateway_moxa_by_sensor_id/`,
        { sensorID },
        { headers: this.headers }
      )
    );
  }

  async matchSensor(sensorID: number, qrNewEdgeDigits: string): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/match_sensor/`,
        { sensorID, qrNewEdge: qrNewEdgeDigits },
        { headers: this.headers }
      )
    );
  }

  async matchAllSensors(qrOldEdgeDigits: string, qrNewEdgeDigits: string): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/match_all_sensors/`,
        { qrOldEdge: qrOldEdgeDigits, qrNewEdge: qrNewEdgeDigits },
        { headers: this.headers }
      )
    );
  }

  async getSensorAdditionalInfo(sensorID: number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/sensor_additional_info/${sensorID}`, {
        headers: this.headers,
      })
    );
  }

  // ==================== Assets ====================

  async getAsset(id: number): Promise<{ asset: any }> {
    return firstValueFrom(
      this.http.get<{ asset: any }>(`${this.cfg.host}api/assets/${id}`, {
        headers: this.headers,
      })
    );
  }

  async getAssetHealth(id: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/assets/${id}`, {
        headers: this.headers,
      })
    );
  }

  // ==================== Equipment ====================

  async createEquipment(params: any): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(`${this.cfg.host}api/create_equipment/`, params, {
        headers: this.headers,
      })
    );
  }

  async updateEquipment(params: any): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(`${this.cfg.host}api/update_equipment/`, params, {
        headers: this.headers,
      })
    );
  }

  async updateEquipmentMarker(markers: any[]): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/update_equipment_markers/`,
        { markers: markers },
        { headers: this.headers }
      )
    );
  }

  async deleteEquipment(equipmentID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.cfg.host}api/delete_equipment/`,
        { equipmentID },
        { headers: this.headers }
      )
    );
  }

  async getNanoEquipmentAccess(assetId: number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/np_check_access/${assetId}`, {
        headers: this.headers,
      })
    );
  }

  // ==================== Plants ====================

  async createPlant(params: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.cfg.host}api/create_plant/`, params, {
        headers: this.headers,
      })
    );
  }

  async updatePlant(objects: IOTObject[]): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/update_plant/`,
        { objects },
        { headers: this.headers }
      )
    );
  }

  async deletePlant(plantID: number): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.cfg.host}api/delete_plant/`,
        { plantID },
        { headers: this.headers }
      )
    );
  }

  async updatePlantMetadata(params: any): Promise<{ StorageLink: string; PlantID: number }> {
    return firstValueFrom(
      this.http.post<{ StorageLink: string; PlantID: number }>(
        `${this.cfg.host}api/update_plant_metadata/`,
        params,
        {
          headers: this.headers,
        }
      )
    );
  }

  async getParentCountryList(): Promise<OTObject[]> {
    return firstValueFrom(
      this.http
        .get<{ count: number; items: OTObject[] }>(`${this.cfg.host}api/parent_country_list/`, {
          headers: this.headers,
        })
        .pipe(map((res: { count: number; items: OTObject[] }) => res.items ?? []))
    );
  }

  // ==================== Building Area Settings ====================

  async getBuildingAreaSettings(objectID: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/building_area_settings/${objectID}`, {
        headers: this.headers,
      })
    );
  }

  async setBuildingAreaSettings(params: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.cfg.host}api/set_building_area_settings/`, params, {
        headers: this.headers,
      })
    );
  }

  // ==================== Additional Info ====================

  async getAdditionalInfoTypes(): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/additional_info_types/`, {
        headers: this.headers,
      })
    );
  }

  async getAdditionalInfo(objectID: number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/additional_info/${objectID}`, {
        headers: this.headers,
      })
    );
  }

  async updateAdditionalInfo(additionalInfo: any): Promise<any[]> {
    return firstValueFrom(
      this.http.post<any[]>(
        `${this.cfg.host}api/update_additional_info/`,
        { additionalInfo },
        { headers: this.headers }
      )
    );
  }

  // ==================== Gateway ====================

  async checkDeviceMapping(deviceValue: string, deviceType: string): Promise<{ isValid: boolean }> {
    return firstValueFrom(
      this.http.post<{ isValid: boolean }>(
        `${this.cfg.host}api/check_device_mapping/`,
        { deviceValue, mappingType: deviceType },
        { headers: this.headers }
      )
    );
  }

  async reserveGatewaySlot(
    gateway: string,
    isMT: boolean,
    isHtst: boolean
  ): Promise<{ id: string }> {
    return firstValueFrom(
      this.http.post<{ id: string }>(
        `${this.cfg.host}api/reserve_gateway_slot/`,
        { gateway, isMT, isHtst },
        { headers: this.headers }
      )
    );
  }

  async checkGatewayStatusMoxa(gateway: string): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${this.cfg.host}api/check_gateway_status_moxa/`,
        { gateway },
        { headers: this.headers }
      )
    );
  }

  async checkGatewayConnectivity(gateway: string): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${this.cfg.host}api/check_gateway_connectivity/`,
        { gateway },
        { headers: this.headers }
      )
    );
  }

  async checkGatewayConnectivityMoxa(gateway: string, dau: string): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${this.cfg.host}api/check_gateway_connectivity_moxa/`,
        { gateway, dau },
        { headers: this.headers }
      )
    );
  }

  async checkDauConnectivity(gateway: string, slot: number): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${this.cfg.host}api/check_dau_connectivity/`,
        { gateway, slot },
        { headers: this.headers }
      )
    );
  }

  async configureGateway(params: any): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(`${this.cfg.host}api/configure_gateway/`, params, {
        headers: this.headers,
      })
    );
  }

  async configureGatewayMoxa(params: any): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(`${this.cfg.host}api/configure_gateway_moxa/`, params, {
        headers: this.headers,
      })
    );
  }

  async configureGatewayMoxaHTST(params: any): Promise<{ success: boolean }> {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${this.cfg.host}api/configure_gateway_moxa_htst/`,
        params,
        { headers: this.headers }
      )
    );
  }

  // ==================== NanoPrecise ====================

  async getNanoPreciseAssetsToken(): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/np/login/`, {
        headers: this.headers,
      })
    );
  }

  async getNanoPreciseAssetsDetail(assetId: number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/nano_precise_assets/${assetId}`, {
        headers: this.headers,
      })
    );
  }

  // ==================== Country ====================

  async getCountryList(): Promise<any[]> {
    return firstValueFrom(this.http.get<any[]>(`${this.cfg.host}api/registration_country_list/`));
  }

  // ==================== Equipment Monitor Specific ====================

  /**
   * Get detailed asset information including notifications
   * @param id - Asset ID
   * @returns Asset detail response
   */
  async getAssetDetail(id: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/assets/${id}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get sensors with telemetry data for an asset
   * @param id - Asset ID
   * @returns Sensor telemetry response
   */
  async getSensorsWithTelemetry(id: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/sensors/${id}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get all notifications
   * @returns Notifications response
   */
  async getNotifications(): Promise<any> {
    if (!this.notificationDataPromise) {
      this.notificationDataPromise = firstValueFrom(
        this.http.get<any>(`${this.cfg.host}api/notification/`, {
          headers: this.headers,
        })
      );
    }
    return this.notificationDataPromise;
  }

  /**
   * Get object history (events/changes)
   * @param objectId - Object ID
   * @param sensorId - Sensor ID
   * @param topN - Number of records to retrieve
   * @returns History items
   */
  async getObjectHistory(objectId: number, sensorId: number, topN = 1000): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/history/${objectId}/${sensorId}/${topN}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get telemetry data for a sensor measurement
   * @param objectID - Object ID
   * @param sensorID - Sensor ID
   * @param measurementID - Measurement type ID
   * @param fromDate - Start date ISO string
   * @param toDate - End date ISO string
   * @returns Telemetry data
   */
  async getTelemetry(
    objectID: number,
    sensorID: number,
    measurementID: number,
    fromDate: string,
    toDate: string
  ): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.cfg.host}api/telemetry/${objectID}/${sensorID}/${measurementID}`,
        { from: fromDate, to: toDate },
        { headers: this.headers }
      )
    );
  }

  /**
   * Get NanoPrecise refresh token for SSO
   * @param assetId - Asset ID
   * @returns Token response
   */
  async getNanoPreciseRefreshToken(assetId: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/sso_login/${assetId}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get NanoPrecise dashboard SSO parameters
   * @param assetId - Asset ID
   * @returns SSO parameters
   */
  async goToNanoPreciseDashboard(assetId: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/np_sso_params/${assetId}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get NanoPrecise asset health status
   * @param assetId - Asset ID
   * @returns Health status
   */
  async getNanoPreciseAssetsHealthStatus(assetId: number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`${this.cfg.host}api/np_health_status/${assetId}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get sensor subscription details
   * @param sensorId - Sensor ID
   * @returns Subscription details
   */
  async getSensorSubscriptionDetails(sensorId: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/subscription/${sensorId}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get customer sensor subscription details
   * @param objectID - Object ID
   * @returns Subscription details
   */
  async getCustomerSensorSubscriptionDetails(objectID: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(`${this.cfg.host}api/customer-subscriptions/${objectID}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Get authenticated frontend keys (MapBox, etc.)
   * @returns Keys object
   */
  async getAuthenticatedFrontendKeys(): Promise<{
    satelliteView: string;
    mapView: string;
    geoCodingToken: string;
    vapIdPublicKey: string;
  }> {
    return firstValueFrom(
      this.http.get<{
        satelliteView: string;
        mapView: string;
        geoCodingToken: string;
        vapIdPublicKey: string;
      }>(`${this.cfg.host}api/authenticated_frontend_keys`, {
        headers: this.headers,
      })
    );
  }
}
